import { ReactNode, useMemo } from 'react'
import classNames from 'classnames'

import { IconElement, Statused } from 'types/ui'
import { StatusSymbol } from './status-symbol'

type InputContainerProps = Statused & {
  disabled?: boolean
  leftIcon?: IconElement
  children: ReactNode
  regular?: boolean
}

export function InputContainer({
  children,
  leftIcon: LeftIcon,
  regular = true,
  disabled = false,
  success = false,
  warning = false,
  error = false,
}: InputContainerProps) {
  const hasPrefix = !!LeftIcon && regular
  return (
    <div className="relative inline-block w-full h-fit text-basic">
      {hasPrefix && LeftIcon && (
        <div className="absolute w-4 h-4 -translate-y-1/2 top-1/2 left-3">
          <LeftIcon
            className={classNames('w-4 h-4 stroke-current', {
              'stroke-zinc-400 dark:stroke-zinc-600': disabled,
            })}
          />
        </div>
      )}
      {children}
      <div className="absolute flex items-center justify-end -translate-y-1/2 top-1/2 right-3">
        {!disabled && <StatusSymbol error={error} warning={warning} success={success} />}
      </div>
    </div>
  )
}

export function useInputClassname({
  className,
  success,
  warning,
  error,
  hasValue,
  textarea = false,
  hasPrefix = false,
  isDate = false,
  regular = true,
}: Statused & {
  className?: string
  textarea?: boolean
  hasPrefix?: boolean
  hasValue?: boolean
  isDate?: boolean
  regular?: boolean
}) {
  const hasRightIcon = regular && (success || error || warning)
  const inputClassname = useMemo(
    () =>
      classNames('input-styles !py-[5px]', className, {
        '!h-[32px] lg:!h-[38px] ': !textarea,
        '!min-h-[32px] lg:!min-h-[38px] ': textarea,
        'state-primary': !(success || warning || error),
        'state-success': success,
        'state-error': error && !success,
        'state-warning': warning && !success && !error,
        'appearance-none': isDate,
        'text-zinc-500 dark:text-zinc-400 appearance-none': isDate && !hasValue,
        '!pl-9': hasPrefix,
        '!pl-3': !hasPrefix,
        '!pr-9': hasRightIcon,
        '!pr-3': !hasRightIcon,
      }),
    [className, success, warning, textarea, error, isDate, hasValue, hasPrefix, hasRightIcon]
  )
  return inputClassname
}
